import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from 'vue'
    import { useId, useRoute } from '@common/composables'
    import { useDashboardPage } from '@RankHR/Regions/pages-core'

    import "@common/Components/ValidationError.vue"
    import WorkspaceNavigation from "@common/Components/WorkspaceNavigation.vue";
    import { guid } from "@app/Guid"
    import { boolCompare, strCompareLowerCase } from "@common/Common";

    
export default /*#__PURE__*/_defineComponent({
  __name: 'DashboardPage',
  setup(__props) {

    const regionId = useId()

    document.title = `Dashboard`;

    const inputRegionName = ref()

    const inputPersonId = ref()
    const inputEmail = ref()
    const inputDisplayName = ref()
    const inputCriteriaName = ref('All Employees & Managers')
    const inputScaleName = ref('Default scale')
    const inputSelectedPeople = ref([])

    const inputSubject = ref()
    const inputMessage = ref()
    const inputIncludeSurveyLink = ref(false)
    const inputIncludeReportLink = ref(false)

    const inputSubMenu = ref(null)
    const inputShowDeletedUsers = ref(false)

    const {
        useAddPersonCommand,
        useCopyPersonVotesCommand,
        useCreateRegionCommand,
        useCreateVotingCycleCommand,
        useDisableAnonymousModeCommand,
        useDisablePersonCommand,
        useEnablePersonCommand,
        useEnableAnonymousModeCommand,
        usePeopleByRegionQuery,
        useRegionQuery,
        useRemoveVotingCycleCommand,
        useSendEmailCommand,
        useSetCriteriaVersionCommand,
        useStartVotingCycleCommand,
        useStopVotingCycleCommand,
        useSwitchVotingCycleCommand,
        useUpdatePersonCommand,
        useVotingProgressQuery,
    } = useDashboardPage()


    const { invokeCreateRegionCommandV1, succeedCreateRegionCommandV1 } = useCreateRegionCommand(regionId, inputRegionName)

    const { invokeDisableAnonymousModeCommandV1, succeedDisableAnonymousModeCommandV1 } = useDisableAnonymousModeCommand(regionId)

    const { invokeEnableAnonymousModeCommandV1, succeedEnableAnonymousModeCommandV1 } = useEnableAnonymousModeCommand(regionId)

    const { invokeSetCriteriaVersionCommandV1 } = useSetCriteriaVersionCommand(regionId)


    const { invokeAddPersonCommandV1, onSucceedAddPersonCommandV1, invokingAddPersonCommandV1, validationErrorsAddPersonCommandV1, succeedAddPersonCommandV1 }
        = useAddPersonCommand(() => guid(), regionId, inputEmail, inputDisplayName, inputCriteriaName, inputScaleName)

    const { invokeUpdatePersonCommandV1, onSucceedUpdatePersonCommandV1, invokingUpdatePersonCommandV1, validationErrorsUpdatePersonCommandV1, succeedUpdatePersonCommandV1 }
        = useUpdatePersonCommand(inputPersonId, regionId, inputEmail, inputDisplayName, inputCriteriaName, inputScaleName)

    const { invokeDisablePersonCommandV1, succeedDisablePersonCommandV1 } = useDisablePersonCommand(inputPersonId, regionId)

    const { invokeEnablePersonCommandV1, succeedEnablePersonCommandV1 } = useEnablePersonCommand(inputPersonId, regionId)

    const { invokeSendEmailCommandV1, succeedSendEmailCommandV1 }
        = useSendEmailCommand(inputSubject, inputMessage, inputSelectedPeople, inputIncludeSurveyLink, inputIncludeReportLink)


    const { createVotingCycleCommandV1, invokeCreateVotingCycleCommandV1, succeedCreateVotingCycleCommandV1 } = useCreateVotingCycleCommand(regionId)

    const { invokeCopyPersonVotesCommandV1 } = useCopyPersonVotesCommand()

    const { invokeRemoveVotingCycleCommandV1, succeedRemoveVotingCycleCommandV1 } = useRemoveVotingCycleCommand(regionId)

    const { invokeStartVotingCycleCommandV1, succeedStartVotingCycleCommandV1 } = useStartVotingCycleCommand(regionId)

    const { invokeStopVotingCycleCommandV1, succeedStopVotingCycleCommandV1 } = useStopVotingCycleCommand(regionId)

    const { invokeSwitchVotingCycleCommandV1, succeedSwitchVotingCycleCommandV1 } = useSwitchVotingCycleCommand(regionId)


    const { modelPeopleByRegionQueryV1, runPeopleByRegionQueryV1 } = usePeopleByRegionQuery(regionId)
    const { modelRegionQueryV1, runRegionQueryV1 } = useRegionQuery(regionId)
    const { modelVotingProgressQueryV1, runVotingProgressQueryV1 } = useVotingProgressQuery(regionId)


    const peopleModel = computed(() => modelPeopleByRegionQueryV1.value)
    const regionModel = computed(() => modelRegionQueryV1.value)
    const progressModel = computed(() => modelVotingProgressQueryV1.value?.Progress || [])

    const people = computed(() => peopleModel.value?.People || [])
    const encodedVotingCycle = computed(() => encodeURIComponent(regionModel.value?.VotingCycle))

    const selectedPeopleEmails = computed<string[] | string>(() => {
        if (inputSelectedPeople.value.length > 0) {
            const emails = inputSelectedPeople.value.map(id => people.value.find(a => a.Id == id).Email)
            return emails.sort()
        }
        return "No recipients"
    })

    const peopleWithProgress = computed(() => {
        return people.value
            .filter(a => inputShowDeletedUsers.value || a.Enabled)
            .map(a => Object.assign({}, a, {
                Progress: getProgress(a.Id)
            }))
            .sort((a, b) => strCompareLowerCase(a.DisplayName, b.DisplayName))
            .sort((a, b) => boolCompare(a.Enabled, b.Enabled));
    })

    const activePeople = computed(() => {
        return peopleWithProgress.value.filter(a => a.Enabled);
    })

    const deletedPeople = computed(() => {
        return peopleWithProgress.value.filter(a => !a.Enabled);
    })


    onMounted(() => {
        runPeopleByRegionQueryV1()
        runRegionQueryV1()
        runVotingProgressQueryV1()
    })

    watch([succeedCreateRegionCommandV1], (succeed) => {
        runPeopleByRegionQueryV1()
        runRegionQueryV1()
        runVotingProgressQueryV1()
    })

    watch(
        [
            succeedDisableAnonymousModeCommandV1,
            succeedEnableAnonymousModeCommandV1,
        ],
        (succeed) => {
            runRegionQueryV1()
        })

    watch(
        [
            succeedAddPersonCommandV1,
            succeedUpdatePersonCommandV1,
            succeedDisablePersonCommandV1,
            succeedEnablePersonCommandV1,
            succeedCreateVotingCycleCommandV1,
            succeedRemoveVotingCycleCommandV1,
            succeedStartVotingCycleCommandV1,
            succeedStopVotingCycleCommandV1,
            succeedSwitchVotingCycleCommandV1,
        ],
        (succeed) => {
            runPeopleByRegionQueryV1()
            runRegionQueryV1()
            runVotingProgressQueryV1()
        })

    onSucceedAddPersonCommandV1(() => {
        inputPersonId.value = null;
        inputEmail.value = null;
        inputDisplayName.value = null;
        inputCriteriaName.value = 'All Employees & Managers';
        inputScaleName.value = 'Default scale';
        inputSubMenu.value = null;
    })

    onSucceedUpdatePersonCommandV1(() => {
        inputPersonId.value = null;
        inputEmail.value = null;
        inputDisplayName.value = null;
        inputCriteriaName.value = 'All Employees & Managers';
        inputScaleName.value = 'Default scale';
        inputSubMenu.value = null;
    })

    watch(succeedCreateVotingCycleCommandV1,
        (succeed) => {
            if (!succeed) return

            if (regionModel.value.VotingCycles && regionModel.value.VotingCycles.length > 0) {
                const sourceVotingCycle = regionModel.value.VotingCycle
                const destVotingCycle = createVotingCycleCommandV1.value.VotingCycle
                const destVotingCycleIdx = regionModel.value.VotingCycles.indexOf(destVotingCycle)

                // only if it is new voting cycle to prevent overriding historical votes
                if (destVotingCycleIdx == -1) {
                    if (confirm(`Do want to copy votes from ${sourceVotingCycle} cycle to ${destVotingCycle} for each person?`)) {
                        for (let p of peopleModel.value.People) {
                            if (p.Enabled) {
                                const id = p.Id
                                invokeCopyPersonVotesCommandV1({ id, sourceVotingCycle, destVotingCycle })
                            }
                        }

                        runVotingProgressQueryV1()
                    }
                }
            }
        })


    function getProgress(personId) {
        const person = progressModel.value.find(b => b.PersonId == personId);
        return person ? person.Progress : 0;
    }

    function handleAddPerson() {
        invokeAddPersonCommandV1()
    }

    function handleEditPerson(person) {
        inputPersonId.value = person.Id;
        inputEmail.value = person.Email;
        inputDisplayName.value = person.DisplayName;
        inputCriteriaName.value = person.CriteriaName;
        inputScaleName.value = person.ScaleName;
        inputSubMenu.value = 'EditPerson';
    }

    function handleUpdatePerson() {
        invokeUpdatePersonCommandV1()
    }

    function handleDisablePerson(id) {
        const person = people.value.find(a => a.Id == id);
        if (confirm(`Do you want to disable ${person.DisplayName}?`)) {
            invokeDisablePersonCommandV1({ Id: id })
        }
    }

    function handleEnablePerson(id) {
        const person = people.value.find(a => a.Id == id);
        if (confirm(`Do you want to re-enable ${person.DisplayName}?`)) {
            invokeEnablePersonCommandV1({ Id: id })
        }
    }

    function handleTooglePeopleSelection() {
        const selected = inputSelectedPeople.value;
        inputSelectedPeople.value = people.value
            .filter(a => a.Enabled && selected.indexOf(a.Id) == -1)
            .map(a => a.Id);
    }

    function handleSendIndividualReports() {
        invokeSendEmailCommandV1()

        inputSubject.value = null;
        inputMessage.value = null;
        inputIncludeSurveyLink.value = false;
        inputIncludeReportLink.value = false;
        inputSubMenu.value = null;
    }

    function handleCreateRegion() {
        invokeCreateRegionCommandV1()
    }

    function handleEnableAnonymousMode() {
        if (confirm(`Do you want to enable anonymous mode? (comments without evaluator name)`)) {
            invokeEnableAnonymousModeCommandV1()
        }
    }

    function handleDisableAnonymousMode() {
        if (confirm(`Do you want to disable anonymous mode? (comments with evaluator name)`)) {
            invokeDisableAnonymousModeCommandV1()
        }
    }

    function handleAddVotingCycle() {
        const votingCycle = prompt("What is a new voting cycle name?\r\n *between 2 and 20 characters long\r\n *only letters, digits, space and dash are allowed")?.trim();
        if (votingCycle) {
            if (/^([a-zA-Z0-9\ \-]{2,20})$/.test(votingCycle)) {
                invokeCreateVotingCycleCommandV1({ VotingCycle: votingCycle });
            }
            else {
                alert("Voting cycle name should be between 2 and 20 characters long. Only Letters and digits are allowed.");
            }
        }
    }

    function handleSwitchVotingCycle(votingCycle) {
        if (confirm(`Do you really want to switch a voting cycle to '${votingCycle}'?`)) {
            invokeSwitchVotingCycleCommandV1({ VotingCycle: votingCycle });
        }
    }

    function handleStartVotingCycle(votingCycle) {
        if (confirm(`Do you really want to start current voting cycle?`)) {
            invokeStartVotingCycleCommandV1({ VotingCycle: votingCycle });
        }
    }

    function handleStopVotingCycle(votingCycle) {
        if (confirm(`Do you really want to stop current voting cycle?`)) {
            invokeStopVotingCycleCommandV1({ VotingCycle: votingCycle });
        }
    }

    function handleRemoveVotingCycle(votingCycle) {
        if (confirm(`Do you really want to delete current ${votingCycle} voting cycle?`)) {
            invokeRemoveVotingCycleCommandV1({ VotingCycle: votingCycle });
        }
    }

    function handleSetCriteriaVersionCommandV1(ver) {
        if (confirm(`Do you want to switch questions to ${ver}? (this may affect reports)`)) {
            invokeSetCriteriaVersionCommandV1({ CriteriaVersion: ver, VotingCycle: encodedVotingCycle.value });
        }
    }

    function handleRefreshProgress() {
        runVotingProgressQueryV1()
    }

return { __sfc: true,regionId, inputRegionName, inputPersonId, inputEmail, inputDisplayName, inputCriteriaName, inputScaleName, inputSelectedPeople, inputSubject, inputMessage, inputIncludeSurveyLink, inputIncludeReportLink, inputSubMenu, inputShowDeletedUsers, useAddPersonCommand, useCopyPersonVotesCommand, useCreateRegionCommand, useCreateVotingCycleCommand, useDisableAnonymousModeCommand, useDisablePersonCommand, useEnablePersonCommand, useEnableAnonymousModeCommand, usePeopleByRegionQuery, useRegionQuery, useRemoveVotingCycleCommand, useSendEmailCommand, useSetCriteriaVersionCommand, useStartVotingCycleCommand, useStopVotingCycleCommand, useSwitchVotingCycleCommand, useUpdatePersonCommand, useVotingProgressQuery, invokeCreateRegionCommandV1, succeedCreateRegionCommandV1, invokeDisableAnonymousModeCommandV1, succeedDisableAnonymousModeCommandV1, invokeEnableAnonymousModeCommandV1, succeedEnableAnonymousModeCommandV1, invokeSetCriteriaVersionCommandV1, invokeAddPersonCommandV1, onSucceedAddPersonCommandV1, invokingAddPersonCommandV1, validationErrorsAddPersonCommandV1, succeedAddPersonCommandV1, invokeUpdatePersonCommandV1, onSucceedUpdatePersonCommandV1, invokingUpdatePersonCommandV1, validationErrorsUpdatePersonCommandV1, succeedUpdatePersonCommandV1, invokeDisablePersonCommandV1, succeedDisablePersonCommandV1, invokeEnablePersonCommandV1, succeedEnablePersonCommandV1, invokeSendEmailCommandV1, succeedSendEmailCommandV1, createVotingCycleCommandV1, invokeCreateVotingCycleCommandV1, succeedCreateVotingCycleCommandV1, invokeCopyPersonVotesCommandV1, invokeRemoveVotingCycleCommandV1, succeedRemoveVotingCycleCommandV1, invokeStartVotingCycleCommandV1, succeedStartVotingCycleCommandV1, invokeStopVotingCycleCommandV1, succeedStopVotingCycleCommandV1, invokeSwitchVotingCycleCommandV1, succeedSwitchVotingCycleCommandV1, modelPeopleByRegionQueryV1, runPeopleByRegionQueryV1, modelRegionQueryV1, runRegionQueryV1, modelVotingProgressQueryV1, runVotingProgressQueryV1, peopleModel, regionModel, progressModel, people, encodedVotingCycle, selectedPeopleEmails, peopleWithProgress, activePeople, deletedPeople, getProgress, handleAddPerson, handleEditPerson, handleUpdatePerson, handleDisablePerson, handleEnablePerson, handleTooglePeopleSelection, handleSendIndividualReports, handleCreateRegion, handleEnableAnonymousMode, handleDisableAnonymousMode, handleAddVotingCycle, handleSwitchVotingCycle, handleStartVotingCycle, handleStopVotingCycle, handleRemoveVotingCycle, handleSetCriteriaVersionCommandV1, handleRefreshProgress, WorkspaceNavigation }
}

})